import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { Bell, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { BoardScope } from "@/api/raise-board-api";
import {
  useMySubscription,
  useToggleSubscription,
  useUnsubscribeFromEmail,
} from "./use-raise-board-actions";

interface SubscribeButtonProps extends BoardScope {
  /**
   * Opens the page's BoardAuthDialog with this reason. Subscribing needs an
   * account — that's where the email address comes from — but a signed-out
   * visitor should get the same in-place dialog every other board action uses,
   * not a redirect off the board.
   */
  onRequestAuth: (reason: string) => void;
  className?: string;
}

/**
 * "Email me when a deal is added to this board."
 *
 * Not rendered on /global: that board aggregates every workspace, so
 * subscribing to it would be a firehose. The backend rejects a subscribe
 * request that names no board, so this is enforced on both sides rather than
 * just by omitting the button.
 *
 * Also handles the ?unsubscribe=<token> link from a board email. The email
 * points back here rather than at an API-hosted page — there is no BACKEND_URL
 * to build one from, and this way the reader lands on the board they left.
 */
export function SubscribeButton({
  slug,
  lookbookPublicId,
  onRequestAuth,
  className,
}: SubscribeButtonProps) {
  const { isLoaded, isSignedIn } = useUser();
  const scope: BoardScope = { slug, lookbookPublicId };

  const subscribed = useMySubscription(isLoaded && isSignedIn, scope);
  const toggle = useToggleSubscription(scope);
  const unsubscribe = useUnsubscribeFromEmail(scope);

  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("unsubscribe");
  // Strict mode mounts effects twice in development, and the mutation is not
  // idempotent from the user's side — two toasts for one click reads as a bug.
  const handled = useRef(false);

  useEffect(() => {
    if (!token || handled.current) return;
    handled.current = true;

    unsubscribe.mutate(token);

    // Drop the param either way: leaving it in the URL means a refresh or a
    // shared link replays it. `replace` so Back doesn't return to it.
    const next = new URLSearchParams(searchParams);
    next.delete("unsubscribe");
    setSearchParams(next, { replace: true });
  }, [token, searchParams, setSearchParams, unsubscribe]);

  const handleClick = () => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      onRequestAuth("Sign in to get an email when a new deal is added.");
      return;
    }
    toggle.mutate();
  };

  return (
    <Button
      type="button"
      variant={subscribed ? "outline" : "default"}
      onClick={handleClick}
      disabled={toggle.isPending}
      className={cn("rounded-full", className)}
      aria-pressed={subscribed}
      title={
        subscribed
          ? "You'll be emailed when a deal is added to this board"
          : "Get an email when a deal is added to this board"
      }
    >
      {subscribed ? <BellRing /> : <Bell />}
      {subscribed ? "Subscribed" : "Subscribe"}
    </Button>
  );
}

export default SubscribeButton;
